import { useEffect, useMemo, useRef } from "react";
import { RoundedBox } from "@react-three/drei";
import * as THREE from "three";
import type { ContaminatedTileState } from "@reagent/sim";
import { PALETTE } from "../palette";
import { useToonMaterial } from "../materials/useToonMaterial";
import { Outlined } from "../materials/Outlined";
import { TILE_SIZE, gridToWorld } from "./gridToWorld";

interface BoardProps {
  cols: number;
  rows: number;
  contaminatedTiles: readonly ContaminatedTileState[];
}

const TABLE_THICKNESS = 0.4;
const LIP_HEIGHT = 0.15;

/**
 * The board reads as a physical workshop table with a lip, not a floating
 * abstract grid — the diegetic framing `04-art-direction.md` asks for. Tile seams are
 * etched in via a thin grid-line overlay rather than separate tile meshes,
 * keeping this to a handful of draw calls.
 *
 * Contaminated tiles are rendered as a single instanced overlay — one decal
 * per tile, placed just above the etched grid lines (y ≈ 0.03), sized TILE_SIZE
 * * 0.9 so the seams remain visible underneath. The material reads as a
 * distinct surface treatment without relying on colour alone.
 */
export function Board({ cols, rows, contaminatedTiles }: BoardProps) {
  const width = cols * TILE_SIZE;
  const depth = rows * TILE_SIZE;
  const centerX = width / 2 - TILE_SIZE / 2;
  const centerZ = depth / 2 - TILE_SIZE / 2;

  const topGeometry = useMemo(
    () => new THREE.BoxGeometry(width + 0.3, TABLE_THICKNESS, depth + 0.3),
    [width, depth],
  );
  const topMaterial = useToonMaterial(PALETTE.boardTop);

  const gridLines = useMemo(() => {
    const points: THREE.Vector3[] = [];
    // Points are already in board space (tile 0,0 at origin), so this geometry
    // is rendered untranslated — the table slab is what gets centered, not this.
    const y = 0.002;
    for (let c = 0; c <= cols; c++) {
      const x = c * TILE_SIZE - TILE_SIZE / 2;
      points.push(new THREE.Vector3(x, y, -TILE_SIZE / 2), new THREE.Vector3(x, y, depth - TILE_SIZE / 2));
    }
    for (let r = 0; r <= rows; r++) {
      const z = r * TILE_SIZE - TILE_SIZE / 2;
      points.push(new THREE.Vector3(-TILE_SIZE / 2, y, z), new THREE.Vector3(width - TILE_SIZE / 2, y, z));
    }
    return new THREE.BufferGeometry().setFromPoints(points);
  }, [cols, rows, width, depth]);

  // Contaminated tile decals: a single instanced overlay for all contaminated
  // tiles. Zero draw calls when empty.
  const contaminationGeometry = useMemo(
    () => new THREE.BoxGeometry(TILE_SIZE * 0.9, 0.015, TILE_SIZE * 0.9),
    [],
  );
  const contaminationMaterial = useToonMaterial(PALETTE.hazardCritical);
  const contaminationMeshRef = useRef<THREE.InstancedMesh>(null);

  useEffect(() => {
    if (!contaminationMeshRef.current || contaminatedTiles.length === 0) return;

    const mesh = contaminationMeshRef.current;
    mesh.count = contaminatedTiles.length;

    const matrix = new THREE.Matrix4();
    contaminatedTiles.forEach((tile, i) => {
      const [x, y, z] = gridToWorld(tile.pos, 0.03);
      matrix.setPosition(x, y, z);
      mesh.setMatrixAt(i, matrix);
    });

    mesh.instanceMatrix.needsUpdate = true;
  }, [contaminatedTiles]);

  return (
    <group>
      {/* Table slab with outline */}
      <group position={[centerX, -TABLE_THICKNESS / 2, centerZ]}>
        <Outlined geometry={topGeometry} thickness={0.02}>
          <mesh geometry={topGeometry} material={topMaterial} receiveShadow castShadow />
        </Outlined>
      </group>

      {/* Lip: a slightly taller, darker rim around the table edge */}
      <RoundedBox
        args={[width + 0.5, LIP_HEIGHT, depth + 0.5]}
        radius={0.04}
        smoothness={2}
        position={[centerX, -TABLE_THICKNESS - LIP_HEIGHT / 2, centerZ]}
        castShadow
        receiveShadow
      >
        <meshToonMaterial color={PALETTE.boardLip} />
      </RoundedBox>

      {/* Etched grid seams */}
      <lineSegments geometry={gridLines}>
        <lineBasicMaterial color={PALETTE.chassisGreyDark} transparent opacity={0.3} />
      </lineSegments>

      {/* Contaminated tile decals: instanced overlay, one per contaminated tile.
          Zero draw calls when contaminatedTiles is empty. */}
      {contaminatedTiles.length > 0 && (
        <instancedMesh
          ref={contaminationMeshRef}
          args={[contaminationGeometry, contaminationMaterial, contaminatedTiles.length]}
          castShadow
          receiveShadow
        />
      )}
    </group>
  );
}
